import { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { useAlert } from '../../core/AlertContext';
import {
    downloadEventRegistrationsExport,
    useEventRegistrationOverview,
} from '../../hooks/eventRegistration';
import { parseBackendError } from '../backendError';
import { EVENT_DIALOG_MAX_WIDTH, EVENT_DIALOG_PAPER_SX } from '../dialogLayout';
import { GREEN_800, primaryButtonSx, secondaryButtonSx } from '../../core/pvtaStyles';

interface EventRegistrationsDialogProps {
    open: boolean;
    eventId: number;
    eventTitle: string;
    onClose: () => void;
}

/**
 * Verwaltungsansicht der Anmeldungen (Spec §16): Teilnehmerzahlen, Aggregation je
 * Bereich fuer die Kueche, Liste der Anmeldungen und CSV-Download. Serverseitig auf
 * Gruppe 1/2 beschraenkt -- der Dialog wird nur aus dem berechtigten Pfad geoeffnet.
 */
const EventRegistrationsDialog: React.FC<EventRegistrationsDialogProps> = ({
    open,
    eventId,
    eventTitle,
    onClose,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { showAlert } = useAlert();
    const {
        registrationOverview,
        isLoadingRegistrationOverview,
        isErrorRegistrationOverview,
    } = useEventRegistrationOverview(eventId, open);
    const [isDownloading, setIsDownloading] = useState(false);

    const handleDownload = async (): Promise<void> => {
        setIsDownloading(true);
        try {
            await downloadEventRegistrationsExport(eventId, eventTitle);
            showAlert({ severity: 'success', text: 'Export wurde heruntergeladen.' });
        } catch (error) {
            showAlert({ severity: 'error', text: parseBackendError(error).message });
        } finally {
            setIsDownloading(false);
        }
    };

    const totals = registrationOverview?.totals;
    const fieldLabels = registrationOverview?.form?.fields ?? [];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            fullScreen={isMobile}
            maxWidth={EVENT_DIALOG_MAX_WIDTH}
            PaperProps={EVENT_DIALOG_PAPER_SX}
        >
            <DialogTitle sx={{ fontWeight: 800 }}>Anmeldungen</DialogTitle>
            <DialogContent dividers>
                {isErrorRegistrationOverview && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        Die Anmeldungen konnten nicht geladen werden.
                    </Alert>
                )}

                {isLoadingRegistrationOverview && (
                    <Typography variant="body2" color="text.secondary">
                        Wird geladen …
                    </Typography>
                )}

                {totals && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                        <Chip label={`${totals.attendees} Teilnehmer`} color="success" />
                        <Chip
                            label={totals.going === 1 ? '1 Zusage' : `${totals.going} Zusagen`}
                            variant="outlined"
                        />
                        <Chip
                            label={
                                totals.guests === 1
                                    ? 'davon 1 Gast'
                                    : `davon ${totals.guests} Gäste`
                            }
                            variant="outlined"
                        />
                    </Stack>
                )}

                {(registrationOverview?.aggregation.length ?? 0) > 0 && (
                    <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: GREEN_800 }}>
                            Zusammenfassung
                        </Typography>
                        <Stack spacing={1.5} sx={{ mb: 2 }}>
                            {registrationOverview?.aggregation.map((field) => (
                                <Box key={field.field_id}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {field.label}
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                        {field.options.map((option) => (
                                            <Chip
                                                key={option.option_id}
                                                label={`${option.label}: ${option.count}`}
                                                size="small"
                                                variant="outlined"
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                        <Divider sx={{ mb: 2 }} />
                    </>
                )}

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: GREEN_800 }}>
                    Teilnehmer im Einzelnen
                </Typography>

                {!isLoadingRegistrationOverview && !isErrorRegistrationOverview && (
                    (registrationOverview?.registrations.length ?? 0) === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Noch keine Zusagen.
                        </Typography>
                    ) : (
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Mitglied</TableCell>
                                        <TableCell>Teilnehmer</TableCell>
                                        <TableCell>Typ</TableCell>
                                        {fieldLabels.map((field) => (
                                            <TableCell key={field.id}>{field.label}</TableCell>
                                        ))}
                                        <TableCell>Kommentar</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {registrationOverview?.registrations.flatMap((registration) =>
                                        registration.participants.map((participant, participantIndex) => (
                                            <TableRow key={`${registration.rsvp_id}-${participantIndex}`}>
                                                <TableCell>{registration.member.name}</TableCell>
                                                <TableCell>{participant.display_name}</TableCell>
                                                <TableCell>
                                                    {participant.kind === 'MEMBER' ? 'Mitglied' : 'Gast'}
                                                </TableCell>
                                                {fieldLabels.map((field) => {
                                                    const selected = participant.selections[String(field.id)] ?? [];
                                                    const labels = field.options
                                                        .filter((option) => selected.includes(option.id))
                                                        .map((option) => option.label);
                                                    return (
                                                        <TableCell key={field.id}>{labels.join(', ')}</TableCell>
                                                    );
                                                })}
                                                <TableCell sx={{ maxWidth: 220, overflowWrap: 'anywhere' }}>
                                                    {participant.comment}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    )
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ ...secondaryButtonSx }}>
                    Schließen
                </Button>
                <Button
                    startIcon={<DownloadRoundedIcon />}
                    onClick={() => void handleDownload()}
                    disabled={isDownloading}
                    sx={{ ...primaryButtonSx }}
                >
                    CSV herunterladen
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EventRegistrationsDialog;
